import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    if (!savedCart) return [];

    try {
      const parsedCart = JSON.parse(savedCart);
      if (!Array.isArray(parsedCart)) return [];

      // A checkout can only belong to one kitchen. If an older saved cart
      // contains multiple kitchens, retain the first kitchen only.
      const firstMerchantItem = parsedCart.find((item) => item.merchant_id);
      if (!firstMerchantItem) return parsedCart;

      return parsedCart.filter(
        (item) =>
          item.merchant_id &&
          String(item.merchant_id) === String(firstMerchantItem.merchant_id)
      );
    } catch (_) {
      return [];
    }
  });

  useEffect(() => {
    // Save cart to localStorage whenever it changes
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (item) => {
    const incomingMerchantId = item.merchant_id;
    const currentMerchantItem = cart.find((cartItem) => cartItem.merchant_id);

    if (
      incomingMerchantId &&
      currentMerchantItem &&
      String(currentMerchantItem.merchant_id) !== String(incomingMerchantId)
    ) {
      return {
        success: false,
        code: 'DIFFERENT_KITCHEN',
        message: `Your basket already contains items from ${currentMerchantItem.merchant_name || 'another kitchen'}. Complete or clear that order before ordering from a different kitchen.`,
      };
    }

    // Do not mix legacy/global store products with a merchant kitchen order.
    if (
      (incomingMerchantId && cart.some((cartItem) => !cartItem.merchant_id)) ||
      (!incomingMerchantId && currentMerchantItem)
    ) {
      return {
        success: false,
        code: 'MIXED_STORE_ORDER',
        message: 'Items from different stores cannot be combined in one order. Complete or clear your current basket first.',
      };
    }

    setCart((prev) => {
      const existingItem = prev.find(
        (i) =>
          i.id === item.id &&
          i.selectedSize === item.selectedSize &&
          i.selectedColor === item.selectedColor &&
          i.format === item.format
      );
      if (existingItem) {
        return prev.map((i) =>
          i.id === item.id &&
          i.selectedSize === item.selectedSize &&
          i.selectedColor === item.selectedColor &&
          i.format === item.format
            ? { ...i, quantity: i.quantity + item.quantity }
            : i
        );
      }
      return [...prev, { ...item, variantKey: `${item.id}-${item.selectedSize || ''}-${item.selectedColor || ''}-${item.format || ''}` }];
    });

    return { success: true };
  };

  const removeFromCart = (variantKey) => {
    setCart((prev) => prev.filter((item) => item.variantKey !== variantKey));
  };

  const updateQuantity = (variantKey, quantity) => {
    if (quantity <= 0) {
      removeFromCart(variantKey);
    } else {
      setCart((prev) =>
        prev.map((item) =>
          item.variantKey === variantKey ? { ...item, quantity } : item
        )
      );
    }
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('cart');
  };

  const getTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const restoreLastOrder = () => {
    const lastOrder = localStorage.getItem('lastOrder');
    return lastOrder ? JSON.parse(lastOrder) : null;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotal,
        restoreLastOrder,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
